<template>
  <SoRadioCards v-model="value">
    <div class="d-flex flex-column gap-4 w-75">
      <template v-for="appointmentType in appointmentTypes">
        <SoTooltip
          v-if="appointmentType.tooltip"
          :key="appointmentType.value"
          :text="appointmentType.tooltip"
          location="end"
        >
          <SoRadioCard
            :value="appointmentType.value"
            :disabled="appointmentType.disabled"
            data-testid="appointment-type-card"
          >
            <template #title>{{ appointmentType.title }}</template>
            {{ appointmentType.description }}
          </SoRadioCard>
        </SoTooltip>
        <SoRadioCard
          v-else
          :key="`no-tooltip-${appointmentType.value}`"
          :value="appointmentType.value"
          :disabled="appointmentType.disabled"
          data-testid="appointment-type-card"
        >
          <template #title>{{ appointmentType.title }}</template>
          {{ appointmentType.description }}
        </SoRadioCard>
      </template>
    </div>
  </SoRadioCards>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SoRadioCards from "@/components/SoRadioCards.vue"
  import SoRadioCard from "@/components/SoRadioCard.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import {
    AppointmentTypeValue,
    APPOINTMENT_TYPE_TITLES,
  } from "@/types/ImbpAppointmentTypes"
  import { SmartMeterEligibilityReasonEnum } from "@/api/backofficeRest/generated/model"
  import { METER_TYPE, SMEX_JOB_NOTES } from "@/constants/imbp"
  import { getMeterTypeKind } from "@/helpers/meterpointHelper"
  import dayjs from "dayjs"

  interface AppointmentType {
    value: AppointmentTypeValue
    title: string
    description: string
    disabled?: boolean
    tooltip?: string
  }

  function s2Key(meterType: string | null, baseKey: string) {
    return meterType === METER_TYPE.SMETS2 ? `${baseKey}_SMETS2` : baseKey
  }

  function buildJobNoteKey(
    isDualFuelSite: boolean,
    eligibleForGas: boolean,
    eligibleForElec: boolean,
    gasMeterType: string | null,
    elecMeterType: string | null,
  ): string | undefined {
    if (isDualFuelSite) {
      if (eligibleForGas && !eligibleForElec)
        return s2Key(gasMeterType, "DUAL_GAS_ONLY")
      if (eligibleForElec && !eligibleForGas)
        return s2Key(elecMeterType, "DUAL_ELEC_ONLY")
      const types = [elecMeterType, gasMeterType]
        .filter(Boolean)
        .sort((a, b) => a!.localeCompare(b!))
      return `DUAL_${types.join("-")}`
    }
    if (eligibleForGas) return s2Key(gasMeterType, "SINGLE_GAS")
    if (eligibleForElec) return s2Key(elecMeterType, "SINGLE_ELEC")
    return undefined
  }

  export default defineComponent({
    components: {
      SoRadioCards,
      SoRadioCard,
      SoTooltip,
    },
    props: {
      modelValue: {
        type: String,
        default: null,
      },
    },
    emits: ["update:model-value"],
    data() {
      return {
        current: useCurrentAccountStore(),
        value: null as string | null,
      }
    },
    computed: {
      slotsAvailableText(): string {
        const hasAvailableSlots =
          this.current.imbpAvailability.data.hasAvailableSlots
        return hasAvailableSlots
          ? `${this.current.imbpAvailability.data.totalSlots} slots available`
          : "No slots available"
      },
      smexTooltip(): string | undefined {
        const eligibility = this.current.smartMeterEligibility.data
        if (!eligibility) return undefined

        if (eligibility.isEligible) {
          return this.eligibleJobNote
        }

        if (
          eligibility.reason ===
          SmartMeterEligibilityReasonEnum.INELIGIBLE_ALREADY_SMART
        ) {
          return this.alreadySmartTooltip
        }

        if (
          eligibility.reason ===
          SmartMeterEligibilityReasonEnum.INELIGIBLE_SWITCHING_AWAY
        ) {
          const endDate = this.current.account.data?.endDate
          if (endDate) {
            const hasSwitched = endDate.isBefore(dayjs())
            const verb = hasSwitched ? "has switched" : "is switching"
            return `This account ${verb} supplier (account end date: ${endDate.format(
              "D MMM YYYY",
            )}).`
          }
          return "This account is switching supplier."
        }

        return undefined
      },
      alreadySmartTooltip(): string | undefined {
        const meterpoints = this.current.imbpAccount.data?.meterpoints ?? []
        const smartMeters = meterpoints.flatMap((mp) =>
          mp.meters
            .filter((m) => m.isSmartMeter)
            .map((m) => ({
              fuel: mp.meterpointType === "MPAN" ? "electric" : "gas",
              generation: m.isSmet2 ? "SMETS2" : "SMETS1",
            })),
        )

        if (smartMeters.length === 0) {
          return "This customer's meters are already smart meters."
        }

        if (smartMeters.length === 1) {
          const meter = smartMeters[0]
          return `The ${meter.fuel} meter is already a ${meter.generation} smart meter.`
        }

        const allSameGeneration = smartMeters.every(
          (m) => m.generation === smartMeters[0].generation,
        )
        if (allSameGeneration) {
          return `Both of this customer's meters are ${smartMeters[0].generation} smart meters.`
        }

        return "This customer's meters are already smart meters."
      },
      eligibleJobNote(): string | undefined {
        const eligibility = this.current.smartMeterEligibility.data
        if (!eligibility) return undefined

        const meterpoints =
          this.onSupplyMeterpoints.length > 0
            ? this.onSupplyMeterpoints
            : (this.current.imbpAccount.data?.meterpoints ?? [])

        const hasElec = meterpoints.some((mp) => mp.meterpointType === "MPAN")
        const hasGas = meterpoints.some((mp) => mp.meterpointType === "MPRN")
        const isDualFuelSite =
          (eligibility.eligibleForGasExchange && hasElec) ||
          (eligibility.eligibleForElectricExchange && hasGas)

        const findMeterType = (meterpointType: string) => {
          const meters = meterpoints
            .filter((mp) => mp.meterpointType === meterpointType)
            .flatMap((mp) => mp.meters)
          return meters.length > 0 ? getMeterTypeKind(meters[0]) : null
        }

        const key = buildJobNoteKey(
          isDualFuelSite,
          eligibility.eligibleForGasExchange,
          eligibility.eligibleForElectricExchange,
          findMeterType("MPRN"),
          findMeterType("MPAN"),
        )

        return key ? SMEX_JOB_NOTES[key] : undefined
      },
      onSupplyMeterpoints() {
        return (this.current.imbpAccount.data?.meterpoints ?? []).filter(
          (mp) => mp.status.text === "On Supply",
        )
      },
      isGasOnlySite(): boolean {
        const meterpoints = this.current.imbpAccount.data?.meterpoints ?? []
        return (
          meterpoints.length > 0 &&
          !meterpoints.some((mp) => mp.meterpointType === "MPAN")
        )
      },
      appointmentTypes(): AppointmentType[] {
        const shouldEnableSmartMeterUpgrade =
          this.current.smartMeterEligibility.data?.isEligible ||
          this.current.smartMeterEligibility.data?.hasComplexMeterSetup ||
          this.current.smartMeterEligibility.data?.hasMopDiscrepancy

        return [
          {
            value: "SMART_METER_UPGRADE",
            title: APPOINTMENT_TYPE_TITLES.SMART_METER_UPGRADE,
            description: this.slotsAvailableText,
            disabled: !shouldEnableSmartMeterUpgrade,
            tooltip: this.smexTooltip,
          },
          {
            value: "NEW_CONNECTION",
            title: APPOINTMENT_TYPE_TITLES.NEW_CONNECTION,
            description: "Bring a completely new meterpoint on supply",
          },
          {
            value: "ISOLATION_SWITCH",
            title: APPOINTMENT_TYPE_TITLES.ISOLATION_SWITCH,
            description:
              "Allows the customer to connect or disconnect their supply",
            disabled: this.isGasOnlySite,
            tooltip: this.isGasOnlySite
              ? "This customer is gas only, and isolation switches are only relevant for electric meters."
              : undefined,
          },
          {
            value: "METER_REMOVAL",
            title: APPOINTMENT_TYPE_TITLES.METER_REMOVAL,
            description: "Completely disconnect a meterpoint from supply",
          },
          {
            value: "METER_RELOCATION",
            title: APPOINTMENT_TYPE_TITLES.METER_RELOCATION,
            description:
              "Move a meter to a different location, e.g. to allow construction work",
          },
          {
            value: "METER_TAILS",
            title: APPOINTMENT_TYPE_TITLES.METER_TAILS,
            description:
              "Upgrade the cables that carry electricity from the meter to the fuse box",
            disabled: this.isGasOnlySite,
            tooltip: this.isGasOnlySite
              ? "This customer is gas only, and meter tails are only relevant for electric meters."
              : undefined,
          },
          {
            value: "METER_TROUBLESHOOTING",
            title: APPOINTMENT_TYPE_TITLES.METER_TROUBLESHOOTING,
            description:
              "Faulty meters, in-home displays, and non-communicating smart meters",
          },
        ]
      },
    },
    watch: {
      value: {
        handler(newValue) {
          if (newValue) {
            this.$emit("update:model-value", newValue)
          }
        },
      },
    },
  })
</script>
